import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getOpponentHistory, updateOpponent, getOpponentDraftProfile, regenerateOpponentDraftProfile, getAiUsage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AiButton from '../components/AiButton';
import { getAiErrorMessage } from '../utils/aiErrors';

/**
 * Shows full history for a single opponent and allows editing their details.
 */
function OpponentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPro = user?.tier === 'pro';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // O1: Opponent Draft Profile
  const [aiUsageRemaining, setAiUsageRemaining] = useState(20);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!isPro) return;
    getAiUsage().then(data => setAiUsageRemaining(data.remaining)).catch(() => {});
  }, [isPro]);

  async function handleGetDraftProfile(regenerate = false) {
    setProfileLoading(true);
    setProfileError(null);
    try {
      const result = regenerate
        ? await regenerateOpponentDraftProfile(id)
        : await getOpponentDraftProfile(id);
      setProfile(result);
      setAiUsageRemaining(result.usageRemaining);
    } catch (err) {
      setProfileError(getAiErrorMessage(err, 'Could not generate a draft profile. Please try again.'));
    } finally {
      setProfileLoading(false);
    }
  }

  // Edit fields
  const [displayName, setDisplayName] = useState('');
  const [nickname, setNickname] = useState('');
  const [notes, setNotes] = useState('');
  const [yearsInLeague, setYearsInLeague] = useState('');
  const [championships, setChampionships] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [expandedDrafts, setExpandedDrafts] = useState(new Set());
  const toggleDraftExpand = (draftId) => {
    setExpandedDrafts(prev => {
      const next = new Set(prev);
      next.has(draftId) ? next.delete(draftId) : next.add(draftId);
      return next;
    });
  };

  useEffect(() => {
    getOpponentHistory(id)
      .then(d => {
        setData(d);
        setDisplayName(d.opponent.displayName || '');
        setNickname(d.opponent.nickname || '');
        setNotes(d.opponent.notes || '');
        setYearsInLeague(d.opponent.yearsInLeague != null ? String(d.opponent.yearsInLeague) : '');
        setChampionships(d.opponent.championships != null ? String(d.opponent.championships) : '');
      })
      .catch(err => setError(err.response?.data?.error?.message || 'Failed to load manager'))
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const updated = await updateOpponent(id, {
        displayName: displayName.trim() || undefined,
        nickname: nickname.trim() || undefined,
        notes: notes.trim() || undefined,
        yearsInLeague: yearsInLeague !== '' ? Number(yearsInLeague) : undefined,
        championships: championships !== '' ? Number(championships) : undefined,
      });
      setData(prev => ({ ...prev, opponent: updated }));
      setSaved(true);
    } catch (err) {
      setSaveError(err.response?.data?.error?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className='loading'>
        <div className='spinner' />
        <p>Loading manager...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className='card' style={{ maxWidth: 640, margin: '0 auto' }}>
        <div className='error-banner'>{error || 'Manager not found.'}</div>
        <button className='btn btn-secondary' style={{ marginTop: 12 }} onClick={() => navigate('/opponents')}>
          Back
        </button>
      </div>
    );
  }

  const { opponent, drafts = [], leagues = [] } = data;
  const realDrafts = drafts.filter(d => !d.mockDraft);
  const mockDrafts = drafts.filter(d => d.mockDraft);

  // Auto-display the persisted profile on load (requirements C6) -- prefer the freshest
  // post-click `profile` state (set by handleGetDraftProfile) over the persisted fields on
  // `opponent`, so a regenerate doesn't require a full re-fetch of getOpponentHistory to show.
  const opponentHasProfile = !!opponent.profileStatus && opponent.profileStatus !== 'NEVER';
  const hasAnyProfile = !!profile || opponentHasProfile;
  const displayedProfile = profile || (opponentHasProfile
    ? { narrative: opponent.lastProfileNarrative, highlights: opponent.lastProfileHighlights }
    : null);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      {/* Back */}
      <button className='btn btn-secondary' style={{ marginBottom: 16 }} onClick={() => navigate('/opponents')}>
        ← Managers
      </button>

      {/* Edit card */}
      <div className='card' style={{ marginBottom: 16 }}>
        <h2 style={{ margin: '0 0 16px' }}>{opponent.displayName}</h2>
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <label style={{ flex: 2, minWidth: 180 }}>
              <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 4 }}>Name</span>
              <input
                className='input'
                style={{ width: '100%' }}
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                required
              />
            </label>
            <label style={{ flex: 1, minWidth: 140 }}>
              <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 4 }}>Nickname</span>
              <input
                className='input'
                style={{ width: '100%' }}
                value={nickname}
                onChange={e => setNickname(e.target.value)}
                placeholder='Optional'
              />
            </label>
          </div>
          <label>
            <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 4 }}>Notes</span>
            <textarea
              className='input'
              style={{ width: '100%', minHeight: 72, resize: 'vertical' }}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder='Any notes about this manager…'
            />
          </label>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <label style={{ flex: 1, minWidth: 120 }}>
              <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 4 }}>Years in league</span>
              <input
                className='input'
                style={{ width: '100%' }}
                type='number'
                min='0'
                value={yearsInLeague}
                onChange={e => setYearsInLeague(e.target.value)}
                placeholder='0'
              />
            </label>
            <label style={{ flex: 1, minWidth: 120 }}>
              <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 4 }}>Championships</span>
              <input
                className='input'
                style={{ width: '100%' }}
                type='number'
                min='0'
                value={championships}
                onChange={e => setChampionships(e.target.value)}
                placeholder='0'
              />
            </label>
          </div>
          {saveError && <div className='error-banner'>{saveError}</div>}
          {saved && <div style={{ color: 'var(--success)', fontSize: '0.875rem' }}>Saved.</div>}
          <div>
            <button className='btn btn-primary' type='submit' disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* O1: Opponent Draft Profile */}
      <div className='card' style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px' }}>Draft Profile</h3>
        <AiButton
          label={hasAnyProfile ? 'Regenerate Scouting Report' : 'Ask the Doctor for a Scouting Report'}
          isPro={isPro}
          usageRemaining={isPro ? aiUsageRemaining : 0}
          freeTooltip='Upgrade to Pro for AI-generated opponent draft profiles'
          onClick={() => handleGetDraftProfile(hasAnyProfile)}
          loading={profileLoading}
          disabled={realDrafts.length === 0}
          disabledTooltip='No real draft history yet for this manager'
        />
        {profileError && <div className='error-banner' style={{ marginTop: 12 }}>{profileError}</div>}
        {displayedProfile && !profileError && (
          <div style={{ marginTop: 12 }}>
            <p style={{ margin: '0 0 8px' }}>{displayedProfile.narrative}</p>
            {displayedProfile.highlights && displayedProfile.highlights.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {displayedProfile.highlights.map((h, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>{h}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Draft history */}
      <div className='card' style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px' }}>Draft History ({realDrafts.length})</h3>
        {realDrafts.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>No drafts linked yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Draft', 'Team slot', 'Picks'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {realDrafts.map(d => (
                <tr key={d.draftId} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 8px' }}>
                    <Link to={`/draft/${d.draftId}`}>{d.draftName || d.draftId}</Link>
                  </td>
                  <td style={{ padding: '6px 8px' }}>Team {d.teamNumber}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--text-muted)' }}>
                    {(() => {
                      const picks = d.picks || [];
                      const expanded = expandedDrafts.has(d.draftId);
                      const visible = expanded ? picks : picks.slice(0, 3);
                      return (
                        <>
                          {visible.map(p => p.playerName).join(', ') || '—'}
                          {picks.length > 3 && (
                            <button
                              style={{ marginLeft: 8, background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0, fontSize: 'inherit' }}
                              onClick={() => toggleDraftExpand(d.draftId)}
                            >
                              {expanded ? 'Show less' : `Show all ${picks.length} picks`}
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Mock draft history */}
      {mockDrafts.length > 0 && (
        <div className='card' style={{ marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 4px' }}>Mock Drafts ({mockDrafts.length})</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 12px' }}>
            AI-simulated picks — not the opponent's real choices
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Draft', 'Team slot', 'Picks'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mockDrafts.map(d => (
                <tr key={d.draftId} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 8px' }}>
                    <Link to={`/draft/${d.draftId}`}>{d.draftName || d.draftId}</Link>
                  </td>
                  <td style={{ padding: '6px 8px' }}>Team {d.teamNumber}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--text-muted)' }}>
                    {(() => {
                      const picks = d.picks || [];
                      const expanded = expandedDrafts.has(d.draftId);
                      const visible = expanded ? picks : picks.slice(0, 3);
                      return (
                        <>
                          {visible.map(p => p.playerName).join(', ') || '—'}
                          {picks.length > 3 && (
                            <button
                              style={{ marginLeft: 8, background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0, fontSize: 'inherit' }}
                              onClick={() => toggleDraftExpand(d.draftId)}
                            >
                              {expanded ? 'Show less' : `Show all ${picks.length} picks`}
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* League history */}
      <div className='card' style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px' }}>League History ({leagues.length})</h3>
        {leagues.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>No leagues linked yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['League', 'Season', 'Team name'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leagues.map(l => (
                <tr key={`${l.leagueId}-${l.teamName}`} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 8px' }}>
                    <Link to={`/league/${l.leagueId}`}>{l.leagueName || l.leagueId}</Link>
                  </td>
                  <td style={{ padding: '6px 8px' }}>{l.seasonYear || '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{l.teamName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default OpponentDetail;
